/* eslint-disable react/jsx-no-useless-fragment */
import {
    MindyKnowledgePermissions,
    MindyLogisticPermissions,
    MindyRolesPermissions,
    MindyUsersPermissions,
} from '@quadminds/mindy-shared';
import { IAuthMeV2OutputDto } from '@quadminds/shared/auth-system';
import { useQueryClient } from '@tanstack/react-query';
import { FC, PropsWithChildren } from 'react';
import { Navigate } from 'react-router-dom';

type AllAvailablePremissions =
    | MindyUsersPermissions
    | MindyRolesPermissions
    | MindyKnowledgePermissions
    | MindyLogisticPermissions;

const RequirePermission: FC<
    PropsWithChildren<{
        permisions?: AllAvailablePremissions[];
        evaluateBy?: 'and' | 'or';
        customEval?: (userInfo: IAuthMeV2OutputDto) => boolean;
    }>
> = ({ permisions = [], children, evaluateBy = 'and', customEval }) => {
    const queryClient = useQueryClient();

    const userInfo: IAuthMeV2OutputDto | undefined = queryClient.getQueryData(['userInfo']); // Retrieve userInfo from the cache

    if (userInfo === undefined) {
        return <Navigate to="/error-out-scope/user-not-found" />;
    }
    if (children === undefined) {
        throw new Error('You need to provide children to the RequirePermission component');
    }
    // Ignore restricted access for impersonated users
    if (userInfo?.originalLoginId && userInfo?.externalId && userInfo?.originalLoginId !== userInfo?.externalId) {
        return <>{children}</>;
    }

    const userPermissions = userInfo?.permissions ?? [];

    const evaluateFunctions = {
        and: (permisions: AllAvailablePremissions[]) =>
            permisions.every((permision) => userPermissions.includes(permision)),
        or: (permisions: AllAvailablePremissions[]) =>
            permisions.some((permision) => userPermissions.includes(permision)),
    };
    if (customEval) {
        return customEval(userInfo) ? <>{children}</> : <Navigate to="/chat" />;
    }

    return evaluateFunctions[evaluateBy](permisions) ? <>{children}</> : <Navigate to="/chat" />;
};

export default RequirePermission;
